import {
  formatSaved,
  savingsClaim,
  type WorkspaceStats,
  type WorkspaceSummary,
} from '@nolens/domain';
import { formatDate, formatNumber } from '@nolens/i18n';
import { cx, useLocale } from '@nolens/ui';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { ChevronRightIcon, ClockIcon, EyeIcon, GuidesIcon, StatsIcon } from '../components/Icons';
import { QueryView } from '../components/Query';
import { niceMax } from '../lib/chart';
import { useWorkspaceSavings, useWorkspaceStats } from '../lib/queries';
import { spaceName } from '../lib/locale';
import { shellText } from '../i18n/shell';
import { statsText } from '../i18n/stats';
import { spaceCrumbs, WithMe } from './workspace';

type Day = WorkspaceStats['last30'][number];
const sum = (d: Day) => d.member + d.public + d.embed;

/** Estatísticas de um espaço (todos os membros); a poupança só para o dono. */
export function StatsPage() {
  const { workspaceId } = useParams();
  const locale = useLocale();
  const t = statsText(locale);
  const st = shellText(locale);
  return (
    <WithMe
      current={workspaceId}
      crumbs={(me) => spaceCrumbs(me, workspaceId, locale, { label: st.stats })}
    >
      {(me) => {
        const ws = me.workspaces.find((w) => w.id === workspaceId);
        if (!ws) return <p className="text-text-2">{t.spaceNotFound}</p>;
        return <Stats ws={ws} />;
      }}
    </WithMe>
  );
}

function Tile({
  icon,
  label,
  value,
  children,
  accent,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  children?: ReactNode;
  accent?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 rounded-[16px] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--border)]">
      <span
        className={cx(
          'inline-flex size-9 shrink-0 items-center justify-center rounded-full',
          accent ? 'bg-accent-soft text-accent-text' : 'bg-surface-2 text-text-2',
        )}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[13px] text-text-2">{label}</p>
        <p
          className={cx(
            'mt-0.5 text-[26px] font-semibold leading-tight tracking-tight',
            accent && 'text-accent-text',
          )}
        >
          {value}
        </p>
        {children}
      </div>
    </div>
  );
}

function Stats({ ws }: { ws: WorkspaceSummary }) {
  const locale = useLocale();
  const t = statsText(locale);
  const q = useWorkspaceStats(ws.id);
  const owner = ws.role === 'owner';
  const savings = useWorkspaceSavings(owner ? ws.id : undefined);
  return (
    <section className="grid gap-8" data-workspace-stats>
      <header>
        <p className="text-[12px] font-semibold uppercase tracking-wide text-text-3">
          {spaceName(ws, locale)}
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">{t.title}</h1>
        <p className="mt-1 text-sm text-text-2">{t.hint}</p>
      </header>
      <QueryView q={q}>
        {(s) => {
          const reads30 = s.last30.reduce((n, d) => n + sum(d), 0);
          return (
            <>
              <div
                className={cx(
                  'grid gap-4 sm:grid-cols-2',
                  owner ? 'xl:grid-cols-4' : 'lg:grid-cols-3',
                )}
              >
                <Tile icon={<EyeIcon />} label={t.reads30} value={formatNumber(locale, reads30)} />
                <Tile
                  icon={<StatsIcon />}
                  label={t.readsTotal}
                  value={formatNumber(locale, s.readsTotal)}
                />
                <Tile
                  icon={<GuidesIcon />}
                  label={t.guides}
                  value={formatNumber(locale, s.guides)}
                />
                {owner && savings.data && (
                  <Tile
                    icon={<ClockIcon />}
                    label={t.timeSaved}
                    value={`≈ ${formatSaved(savings.data.savedSeconds, locale)}`}
                    accent
                  >
                    <Link
                      to={`/w/${ws.id}/poupanca`}
                      className="mt-1 inline-flex items-center gap-0.5 text-[12.5px] font-medium text-accent-text hover:underline"
                    >
                      {t.seeSavings}
                      <ChevronRightIcon size={13} />
                    </Link>
                  </Tile>
                )}
              </div>
              {owner && savings.data && (
                <p className="-mt-6 text-[12.5px] text-text-3">{savingsClaim(locale)}</p>
              )}
              <ReadsChart days={s.last30} />
              <TopGuides top={s.top} />
            </>
          );
        }}
      </QueryView>
    </section>
  );
}

/**
 * Leituras por dia: uma coluna por dia na cor de ação (uma só série, sem legenda), com o
 * detalhe por origem ao passar o rato ou com o teclado, e a mesma informação em tabela.
 */
function ReadsChart({ days }: { days: Day[] }) {
  const locale = useLocale();
  const t = statsText(locale);
  const [hover, setHover] = useState<number | null>(null);
  const top = niceMax(Math.max(0, ...days.map(sum)));
  const short = (day: string) =>
    formatDate(locale, day + 'T12:00:00', { day: 'numeric', month: 'short' });
  const by = days.reduce(
    (a, d) => ({
      member: a.member + d.member,
      public: a.public + d.public,
      embed: a.embed + d.embed,
    }),
    { member: 0, public: 0, embed: 0 },
  );
  const h = hover === null ? null : days[hover]!;
  return (
    <section
      aria-labelledby="reads-per-day"
      className="rounded-[16px] bg-surface-1 p-5 shadow-[inset_0_0_0_1px_var(--border)]"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="reads-per-day" className="font-semibold">
          {t.perDay}
        </h2>
        <p className="text-[13px] text-text-3">{t.perDayHint}</p>
      </div>
      <div className="mt-5 flex gap-3">
        {/* Eixo: 0, metade e o teto, em números redondos */}
        <div
          aria-hidden="true"
          className="flex h-44 flex-col justify-between text-right text-[11.5px] tabular-nums text-text-3"
        >
          <span className="-translate-y-1/2">{formatNumber(locale, top)}</span>
          <span>{formatNumber(locale, top / 2)}</span>
          <span className="translate-y-1/2">0</span>
        </div>
        <div className="relative min-w-0 flex-1">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex flex-col justify-between"
          >
            <span className="border-t border-border" />
            <span className="border-t border-border" />
            <span className="border-t border-border-strong" />
          </div>
          <ol
            aria-label={t.chartLabel(top)}
            className="relative flex h-44 items-end gap-[2px]"
            onPointerLeave={() => setHover(null)}
          >
            {days.map((d, i) => {
              const n = sum(d);
              const pct = (n / top) * 100;
              return (
                <li
                  key={d.day}
                  tabIndex={0}
                  aria-label={t.dayLabel(short(d.day), n, d.member, d.public, d.embed)}
                  onPointerEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                  className="group flex h-full min-w-0 flex-1 cursor-default items-end justify-center rounded-[4px] outline-none focus-visible:bg-surface-2"
                >
                  <span
                    className={cx(
                      'block w-full max-w-6 transition-opacity',
                      n ? 'rounded-t-[4px] bg-accent' : 'h-[2px] rounded-full bg-border',
                      hover !== null && hover !== i && 'opacity-55',
                    )}
                    style={n ? { height: `${Math.max(pct, 2)}%` } : undefined}
                  />
                </li>
              );
            })}
          </ol>
          {h && hover !== null && (
            <div
              role="presentation"
              className={cx(
                'pointer-events-none absolute -top-2 z-10 w-max -translate-y-full rounded-[10px] bg-surface-1 px-3 py-2 text-[12.5px] shadow-[0_8px_24px_rgb(0_0_0/0.14),inset_0_0_0_1px_var(--border)]',
                // Junto às pontas, o aviso encosta para dentro em vez de sair do cartão
                hover < 5
                  ? '-translate-x-4'
                  : hover > days.length - 6
                    ? '-translate-x-[calc(100%-1rem)]'
                    : '-translate-x-1/2',
              )}
              style={{ left: `${((hover + 0.5) / days.length) * 100}%` }}
            >
              <p className="text-text-3">{short(h.day)}</p>
              <p className="text-[15px] font-semibold text-text-1">{t.reads(sum(h))}</p>
              <p className="mt-0.5 text-text-2">
                {t.team} {h.member} · {t.publicLink} {h.public} · {t.embed} {h.embed}
              </p>
            </div>
          )}
          <div aria-hidden="true" className="mt-2 flex justify-between text-[11.5px] text-text-3">
            <span>{short(days[0]!.day)}</span>
            <span>{short(days[Math.floor(days.length / 2)]!.day)}</span>
            <span>{short(days[days.length - 1]!.day)}</span>
          </div>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-border pt-4 text-sm">
        <span className="text-text-3">{t.bySource}</span>
        {(
          [
            [t.team, by.member],
            [t.publicLink, by.public],
            [t.embed, by.embed],
          ] as const
        ).map(([label, n]) => (
          <span key={label} className="text-text-2">
            {label} <strong className="font-semibold text-text-1">{formatNumber(locale, n)}</strong>
          </span>
        ))}
      </div>
      <details className="mt-3 text-sm">
        <summary className="cursor-pointer text-text-2 hover:text-text-1">{t.asTable}</summary>
        <div className="mt-3 max-h-72 overflow-auto rounded-[10px] shadow-[inset_0_0_0_1px_var(--border)]">
          <table className="w-full text-left text-[13px] tabular-nums">
            <thead className="sticky top-0 bg-surface-1 text-text-3">
              <tr>
                <th className="px-3 py-2 font-medium">{t.day}</th>
                <th className="px-3 py-2 text-right font-medium">{t.team}</th>
                <th className="px-3 py-2 text-right font-medium">{t.publicLink}</th>
                <th className="px-3 py-2 text-right font-medium">{t.embed}</th>
                <th className="px-3 py-2 text-right font-medium">{t.total}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[...days].reverse().map((d) => (
                <tr key={d.day}>
                  <td className="px-3 py-1.5">{short(d.day)}</td>
                  <td className="px-3 py-1.5 text-right">{d.member}</td>
                  <td className="px-3 py-1.5 text-right">{d.public}</td>
                  <td className="px-3 py-1.5 text-right">{d.embed}</td>
                  <td className="px-3 py-1.5 text-right font-medium">{sum(d)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}

/** Os guias mais lidos nos últimos 30 dias, com uma barra relativa ao primeiro. */
function TopGuides({ top }: { top: WorkspaceStats['top'] }) {
  const locale = useLocale();
  const t = statsText(locale);
  const max = Math.max(1, ...top.map((g) => g.reads30));
  return (
    <section
      aria-labelledby="top-guides"
      className="rounded-[16px] bg-surface-1 p-5 shadow-[inset_0_0_0_1px_var(--border)]"
    >
      <h2 id="top-guides" className="font-semibold">
        {t.top}
      </h2>
      <p className="mt-0.5 text-[13px] text-text-3">{t.topHint}</p>
      {top.length === 0 ? (
        <p className="mt-4 text-sm text-text-2">{t.noReads}</p>
      ) : (
        <ol className="mt-4 grid gap-1" data-top-guides>
          {top.map((g, i) => (
            <li key={g.guideId}>
              <Link
                to={`/g/${g.guideId}`}
                className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto] items-center gap-3 rounded-[10px] px-2 py-2 hover:bg-surface-2"
              >
                <span className="text-center text-[13px] font-semibold tabular-nums text-text-3">
                  {i + 1}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[14.5px] font-medium">
                    {g.title || t.untitled}
                  </span>
                  <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-surface-2">
                    <span
                      className="block h-full rounded-full bg-accent"
                      style={{ width: `${(g.reads30 / max) * 100}%` }}
                    />
                  </span>
                </span>
                <span className="text-right">
                  <span className="block text-[14.5px] font-semibold tabular-nums">
                    {formatNumber(locale, g.reads30)}
                  </span>
                  <span className="block text-[12px] text-text-3">{t.allTime(g.total)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
